import { useState, useEffect, useCallback } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, FileText, Download, Trash2, Save, Settings2, Mail, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { formatCurrency } from "../../lib/formatters";
import { useConfirm } from "../ui/confirm-dialog";
import CertifiedPayrollBuilder from "./CertifiedPayrollBuilder";
import MarkSentDialog from "./billing/MarkSentDialog";
import { positionEmails, buildCertifiedPayrollMailto, openMailto } from "../../lib/jobContacts";
import { openAuthenticatedPdf } from "../../lib/openPdf";

const pad = (n) => String(n).padStart(2, "0");
const nowForm = () => { const d = new Date(); return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }; };
const fmtSent = (date, time) => {
  const [y, m, d] = (date || "").split("-");
  if (!y) return "";
  let t = "";
  if (time) { const [hh, mm] = time.split(":").map(Number); t = ` ${((hh + 11) % 12) + 1}:${pad(mm)} ${hh >= 12 ? "PM" : "AM"}`; }
  return `${m}/${d}${t}`;
};

// "Sent 06/12 3:14 PM (×2)" — latest send, hover lists every dispatch.
const SentStamp = ({ p }) => {
  const log = p.sent_log || [];
  if (log.length === 0) return null;
  const last = log[log.length - 1];
  const title = log.map((s) => `${fmtSent(s.date, s.time)}${s.by ? ` — ${s.by}` : ""}${s.recipients?.length ? ` → ${s.recipients.join(", ")}` : ""}`).join("\n");
  return (
    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 cursor-help" title={title} data-testid={`cp-sent-${p.id}`}>
      <Send className="w-3 h-3 mr-1" /> Sent {fmtSent(last.date, last.time)}{log.length > 1 ? ` (×${log.length})` : ""}
    </Badge>
  );
};

const EMPTY_SETUP = { enabled: false, contractor_type: "sub", project_no: "", wage_determination_no: "", project_location: "", classifications: [] };

const openPdf = async (jobId, payrollId, download = false) => {
  try {
    if (download) {
      const res = await apiClient.get(`/jobs/${jobId}/certified-payrolls/${payrollId}/pdf`, { params: { download: true }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `WH-347_${payrollId}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } else {
      await openAuthenticatedPdf(`/jobs/${jobId}/certified-payrolls/${payrollId}/pdf`, { filename: `WH-347_${payrollId}.pdf` });
    }
  } catch { toast.error("Could not open PDF"); }
};

const SetupCard = ({ job, canManageJobs, onSaved }) => {
  const [setup, setSetup] = useState({ ...EMPTY_SETUP, ...(job.certified_payroll || {}) });
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { apiClient.get(`/settings/options`).then((r) => setRoles(r.data.employee_roles || [])).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/jobs/${job.id}`, { certified_payroll: setup });
      toast.success("Certified payroll settings saved");
      onSaved(setup);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const updateCls = (i, patch) => setSetup((s) => ({ ...s, classifications: s.classifications.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const toggleRole = (i, role) => updateCls(i, { roles: (setup.classifications[i].roles || []).includes(role) ? setup.classifications[i].roles.filter((r) => r !== role) : [...(setup.classifications[i].roles || []), role] });

  return (
    <Card data-testid="cp-setup-card">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Settings2 className="w-5 h-5" /> Davis-Bacon Setup</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={!!setup.enabled} onCheckedChange={(v) => setSetup({ ...setup, enabled: v })} disabled={!canManageJobs} data-testid="cp-enabled-switch" />
          <Label>This job requires certified payroll (WH-347)</Label>
        </div>
        {setup.enabled && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>We are the</Label>
                <div className="flex gap-2">
                  {["prime", "sub"].map((t) => (
                    <Button key={t} type="button" size="sm" variant={setup.contractor_type === t ? "default" : "outline"} onClick={() => setSetup({ ...setup, contractor_type: t })} data-testid={`cp-type-${t}`}>
                      {t === "prime" ? "Prime Contractor" : "Subcontractor"}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1"><Label>Project No. / Contract No.</Label><Input value={setup.project_no} onChange={(e) => setSetup({ ...setup, project_no: e.target.value })} data-testid="cp-project-no" /></div>
              <div className="space-y-1"><Label>Wage Determination No.</Label><Input value={setup.wage_determination_no} onChange={(e) => setSetup({ ...setup, wage_determination_no: e.target.value })} placeholder="e.g., TX20250045" data-testid="cp-wd-no" /></div>
              <div className="space-y-1"><Label>Project Location</Label><Input value={setup.project_location} onChange={(e) => setSetup({ ...setup, project_location: e.target.value })} placeholder={[job.city, job.state].filter(Boolean).join(", ") || "City, State"} data-testid="cp-location" /></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Labor classifications (from the wage determination)</Label>
                {canManageJobs && <Button size="sm" variant="outline" onClick={() => setSetup({ ...setup, classifications: [...setup.classifications, { name: "", base_rate: "", fringe_rate: "", roles: [] }] })} data-testid="cp-add-classification"><Plus className="w-4 h-4 mr-1" />Add</Button>}
              </div>
              {setup.classifications.length === 0 && <p className="text-sm text-muted-foreground">No classifications yet — employee roles will be printed as-is. Add one to map roles (e.g., Mason → "Bricklayer") and record the determination's base/fringe rates.</p>}
              {setup.classifications.map((c, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2" data-testid={`cp-classification-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_40px] gap-2 items-end">
                    <div><Label className="text-xs">Classification name</Label><Input value={c.name} onChange={(e) => updateCls(i, { name: e.target.value })} placeholder="Bricklayer" data-testid={`cp-cls-name-${i}`} /></div>
                    <div><Label className="text-xs">WD base rate</Label><Input type="number" step="0.01" value={c.base_rate} onChange={(e) => updateCls(i, { base_rate: e.target.value })} data-testid={`cp-cls-base-${i}`} /></div>
                    <div><Label className="text-xs">WD fringe rate</Label><Input type="number" step="0.01" value={c.fringe_rate} onChange={(e) => updateCls(i, { fringe_rate: e.target.value })} data-testid={`cp-cls-fringe-${i}`} /></div>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setSetup({ ...setup, classifications: setup.classifications.filter((_, j) => j !== i) })} data-testid={`cp-cls-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs text-muted-foreground mr-1 self-center">Applies to roles:</span>
                    {roles.map((r) => (
                      <button key={r} type="button" onClick={() => toggleRole(i, r)} className={`px-2 py-0.5 rounded-full text-xs border ${(c.roles || []).includes(r) ? "bg-slate-800 text-white border-slate-800" : "bg-white hover:bg-slate-50"}`} data-testid={`cp-cls-role-${i}-${r.toLowerCase()}`}>{r}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {canManageJobs && <Button onClick={save} disabled={saving} data-testid="cp-setup-save"><Save className="w-4 h-4 mr-2" />Save Setup</Button>}
      </CardContent>
    </Card>
  );
};

const CertifiedPayrollTab = ({ job, canManageJobs, onJobUpdated }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [sentTarget, setSentTarget] = useState(null);
  const [sentForm, setSentForm] = useState(nowForm());
  const [savingSent, setSavingSent] = useState(false);
  const confirm = useConfirm();

  const emailPayroll = (p) => {
    const emails = positionEmails(job?.contacts, "certified_payroll");
    if (emails.length === 0) {
      toast.warning("No Certified Payroll contact with an email on this job. Add one under Edit Job → Contact Information first.", { duration: 8000, id: "cp-no-contact" });
      return;
    }
    openMailto(buildCertifiedPayrollMailto(job, p, emails), "cp-mailto-link");
    setSentForm(nowForm());
    setSentTarget({ ...p, recipients: emails });
  };

  const saveSent = async () => {
    if (!sentTarget || !sentForm.date) return;
    setSavingSent(true);
    try {
      const r = await apiClient.post(`/jobs/${job.id}/certified-payrolls/${sentTarget.id}/sent`, { date: sentForm.date, time: sentForm.time || null, recipients: sentTarget.recipients || [] });
      setPayrolls((list) => list.map((x) => (x.id === r.data.id ? r.data : x)));
      toast.success(`Payroll #${sentTarget.payroll_no} marked as sent`);
      setSentTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log the send"); } finally { setSavingSent(false); }
  };
  const setup = job.certified_payroll || EMPTY_SETUP;

  const load = useCallback(async () => {
    try { const r = await apiClient.get(`/jobs/${job.id}/certified-payrolls`); setPayrolls(r.data || []); } catch { /* ignore */ }
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    const ok = await confirm({ title: `Delete payroll #${p.payroll_no}?`, description: "The WH-347 PDF and its copy on the Documents tab will be removed.", confirmText: "Delete", confirmVariant: "destructive" });
    if (!ok) return;
    try { await apiClient.delete(`/jobs/${job.id}/certified-payrolls/${p.id}`); toast.success("Payroll deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="certified-payroll-tab">
      <SetupCard job={job} canManageJobs={canManageJobs} onSaved={(s) => onJobUpdated?.({ ...job, certified_payroll: s })} />

      <Card data-testid="cp-history-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Certified Payrolls ({payrolls.length})</CardTitle>
          {canManageJobs && <Button onClick={() => setBuilderOpen(true)} disabled={!setup.enabled} data-testid="cp-new-payroll-btn"><Plus className="w-4 h-4 mr-2" />New Payroll</Button>}
        </CardHeader>
        <CardContent>
          {!setup.enabled ? (
            <p className="text-sm text-muted-foreground" data-testid="cp-disabled-hint">Turn on certified payroll in the setup above to start filing WH-347s for this job.</p>
          ) : payrolls.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="cp-empty">No payrolls yet. Click "New Payroll", pick the week ending Saturday, review, and generate.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>No.</TableHead><TableHead>Week Ending</TableHead><TableHead>Workers</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Gross (project)</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {payrolls.map((p) => (
                  <TableRow key={p.id} data-testid={`cp-row-${p.id}`}>
                    <TableCell className="font-medium">#{p.payroll_no}</TableCell>
                    <TableCell>{p.week_ending}</TableCell>
                    <TableCell>{p.no_work ? "—" : p.worker_count}</TableCell>
                    <TableCell className="text-right font-mono">{p.no_work ? "—" : p.total_hours}</TableCell>
                    <TableCell className="text-right font-mono">{p.no_work ? "—" : formatCurrency(p.gross_project)}</TableCell>
                    <TableCell className="space-x-1">
                      {p.no_work && <Badge variant="outline">No work</Badge>}
                      {p.is_final && <Badge className="bg-emerald-600">Final</Badge>}
                    </TableCell>
                    <TableCell><SentStamp p={p} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" title="View PDF" onClick={() => openPdf(job.id, p.id)} data-testid={`cp-view-${p.id}`}><FileText className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" title="Download" onClick={() => openPdf(job.id, p.id, true)} data-testid={`cp-download-${p.id}`}><Download className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-sky-600" title="Email to Certified Payroll contacts" onClick={() => emailPayroll(p)} data-testid={`cp-email-${p.id}`}><Mail className="w-4 h-4" /></Button>
                      {canManageJobs && <Button variant="ghost" size="icon" className="text-red-600" title="Delete" onClick={() => remove(p)} data-testid={`cp-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MarkSentDialog
        open={!!sentTarget}
        onOpenChange={(o) => { if (!o) setSentTarget(null); }}
        selectedBilling={sentTarget}
        title={`Mark as Sent – Payroll #${sentTarget?.payroll_no ?? ""}`}
        form={sentForm}
        onFormChange={setSentForm}
        onSubmit={saveSent}
        isSubmitting={savingSent}
      />

      {builderOpen && <CertifiedPayrollBuilder job={job} onClose={() => setBuilderOpen(false)} onCreated={(p) => { setBuilderOpen(false); load(); openPdf(job.id, p.id); }} />}
    </div>
  );
};

export default CertifiedPayrollTab;
